import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  displayName: string;
}

interface CurrentUserContextType {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;
}

const CurrentUserContext = createContext<CurrentUserContextType | undefined>(undefined);

export const CurrentUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accounts, instance, inProgress } = useMsal();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchCurrentUser = async () => {
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      if (accounts.length === 0) {
        if (!cancelled) {
          setUser(null);
          setError(null);
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setLoading(true);
        setError(null);
      }

      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0],
        });

        const functionAppUrl =
          import.meta.env.VITE_FUNCTION_APP_URL ||
          'https://func-axis-referral-dev.azurewebsites.net';
        const response = await fetch(`${functionAppUrl}/api/me`, {
          headers: {
            Authorization: `Bearer ${tokenResponse.idToken || tokenResponse.accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user info (${response.status})`);
        }

        const data = await response.json();
        if (!cancelled) {
          setUser(data);
          setError(null);
          setLoading(false);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        if (!cancelled) {
          setUser(null);
          setError(message);
          setLoading(false);
        }
      }
    };

    fetchCurrentUser();

    return () => {
      cancelled = true;
    };
  }, [accounts, instance, inProgress]);

  return (
    <CurrentUserContext.Provider value={{ user, loading, error }}>
      {children}
    </CurrentUserContext.Provider>
  );
};

export function useCurrentUser() {
  const context = useContext(CurrentUserContext);
  if (context === undefined) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider');
  }
  return context;
}
